import { ComposeStepState, IComposeStep } from ".";
import { is } from "immutable";
import _ from "lodash";
import * as moment from "moment";
import * as React from "react";
import * as BS from "react-bootstrap";
import { AbstractTypeMediaEditor } from "./AbstractTypeMediaEditor";
import { ContinueButtons } from "./ContinueButtons";
import { ComposeStepProps } from "./IComposeStep";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { Utility } from "../../../../utils";

type OwnProps = ComposeStepProps & {
    onMediaDeleted: (abstractMediaId: number) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    updateAbstractMedia: (media: M.Abstract.AbstractMedia) => void;
};

type OwnState = ComposeStepState & {
    uploadStatus: number[];
};

export class UploadsForm extends ComponentWithValidatedForm<OwnProps, OwnState> implements IComposeStep {

    private _isMounted = false;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.Abstract.ComposeUploads();
        this.state = {
            ...this.getInitialState(formModel),
            isSubmitLater: false,
            uploadStatus: [],
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        if (this.props.type)
            this.internalSetFormData(this.props.type, this.props.abstract);

        this.initialChecks();
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
        this.initialChecks(prevProps);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public setFormData(abstract: M.Abstract.Abstract) {
        // not used
    }

    public updateMediaForm(media: M.Abstract.AbstractMedia) {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeUploads;
        for (const tm of formModel.data.typeMedia) {
            const mediaForm = _.find(tm.media, (m) => m.mediaId === media.id);
            if (mediaForm) {
                mediaForm.caption = mediaForm.caption || media.caption;
                mediaForm.embedUrl = media.embedUrl;
                mediaForm.fileName = media.fileName;
                mediaForm.height = media.height;
                mediaForm.id = media.abstractMediaId;
                mediaForm.isImage = media.isImage;
                mediaForm.isProcessing = media.isProcessing;
                mediaForm.isVideo = media.isVideo;
                mediaForm.tags = mediaForm.tags.length
                    ? mediaForm.tags
                    : media.tags.map((x) => ({
                        id: x.id,
                        label: x.name,
                        name: x.name,
                        value: x.id,
                    }));
                mediaForm.title = media.title;
                mediaForm.typeId = media.typeId;
                mediaForm.url = media.url;
                mediaForm.width = media.width;
            }
        }

        this.setState({
            ...this.state,
            formModel,
        });
    }

    public render() {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeUploads;

        const uploadDeadline = this.props.type
                && this.props.type.uploadDeadline
                && this.props.type.typeMedia.filter((tm) => tm.requiredForSubmit).length
            ? <BS.Alert bsStyle="info">
                <p>
                    <Comp.Generic.Icon icon="info-circle" /> Upload your file(s)
                    by <strong>{moment(Utility.utcToLocal(this.props.type.uploadDeadline)).format("MMMM D, YYYY ")}</strong>
                    or your abstract will be incomplete.
                </p>
            </BS.Alert>
            : null;

        const isSubmitDeadlinePassed = this.props.type && M.Abstract.isSubmitDeadlinePassed(this.props.type);

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onCustomValidation={() => this.handleValidation()}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            <h1>Uploads</h1>

            {uploadDeadline}

            {formModel.data.typeMedia.map((t, i) => (
                <AbstractTypeMediaEditor
                    key={i}
                    abstractGuid={this.props.abstract ? this.props.abstract.guid : ""}
                    abstractTypeMedia={t}
                    api={this.props.api}
                    formComponent={this}
                    parentFieldName={`typeMedia[${t.index}]`}
                    type={this.props.type}
                    onMediaDeleted={(abstractMediaId) => this.props.onMediaDeleted(abstractMediaId)}
                    onUploadCompleted={(abstractTypeMediaId) => this.updateUploadStatus(abstractTypeMediaId, true)}
                    onUploadStarted={(abstractTypeMediaId) => this.updateUploadStatus(abstractTypeMediaId, false)}
                    subscribeToMediaProcessing={this.props.subscribeToMediaProcessing}
                    updateAbstractMedia={this.props.updateAbstractMedia}
                />))}

            <ContinueButtons
                disableSubmit={this.state.uploadStatus.length > 0}
                onBack={() => this.props.onBack()}
                onSave={(isSubmitLater) => this.onSaveHandler(isSubmitLater)}
                onSkip={() => this.props.onSkip()}
                hideBack={this.state.uploadStatus.length > 0 || isSubmitDeadlinePassed}
                hideSaveLater={this.state.uploadStatus.length > 0 || isSubmitDeadlinePassed}
                hideSkip={this.state.uploadStatus.length > 0 || isSubmitDeadlinePassed}
                processing={this.state.formProcessing}
                saveLabel={isSubmitDeadlinePassed ? "Save" : undefined}
            />

        </Comp.Validation.ValidatedForm>;
    }

    private initialChecks(prevProps?: Readonly<OwnProps>) {
        if (prevProps) {
            if ((!is(prevProps.type, this.props.type) || !is(prevProps.abstract, this.props.abstract)) && this.props.type && this.props.abstract) {
                // both abstract and type are available, so set form data
                this.internalSetFormData(this.props.type, this.props.abstract);
            }

            // if no form for this type, skip to next step
            if (!is(prevProps.type, this.props.type) && this.props.type && !this.props.type.typeMedia.length) {
                console.info(`No uploads required for type ${this.props.type.name}. Skipping to review.`);
                this.props.onSkip();
            }
        }
        else {
            // if no form for this type, skip to next step
            if (this.props.type && !this.props.type.typeMedia.length) {
                console.info(`No uploads required for type ${this.props.type.name}. Skipping to review.`);
                this.props.onSkip();
            }
        }
    }

    private internalSetFormData(type: M.Abstract.AbstractType, abstract?: M.Abstract.Abstract) {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeUploads;
        formModel.data.typeMedia = type.typeMedia.map((tm, i) => {
            return {
                description: tm.description,
                id: tm.id,
                index: i,
                label: tm.label,
                labelHint: tm.labelHint,
                maxUploads: tm.maxUploads,
                maxSize: tm.maxSize,
                minUploads: tm.minUploads,
                media: abstract && abstract.media
                    .filter((m) => m.abstractTypeMediaId === tm.id)
                    .map((m, j) => ({
                        caption: m.caption,
                        deleted: false,
                        embedUrl: m.embedUrl,
                        fileName: m.fileName,
                        height: m.height,
                        id: m.abstractMediaId,
                        index: j,
                        isImage: m.isImage,
                        isProcessing: m.isProcessing,
                        isVideo: m.isVideo,
                        mediaId: m.id,
                        tags: m.tags
                            ? m.tags.map((x) => ({
                                id: x.id,
                                label: x.name,
                                name: x.name,
                                value: x.id,
                            }))
                            : [],
                        thumbMd: m.thumbMd,
                        thumbSm: m.thumbSm,
                        title: m.title,
                        typeId: m.typeId,
                        url: m.url,
                        width: m.width,
                    })) || [],
                mediaType: tm.mediaType,
                mediaTypeExtns: tm.mediaTypeExtns,
                mediaTypeId: tm.mediaTypeId,
                promptCaption: tm.promptCaption,
                promptKeywords: tm.promptKeywords,
                promptTitle: tm.promptTitle,
                requiredAfterAccept: tm.requiredAfterAccept,
                requiredForSubmit: tm.requiredForSubmit,
            };
        });

        // add empty media
        for (const t of formModel.data.typeMedia) {
            if (!t.media.length)
                t.media.push({ ...Forms.Abstract.DefaultAbstractMedia });
        }

        this.setState({
            ...this.state,
            formModel,
        });
    }

    private onSaveHandler(isSubmitLater: boolean) {
        this.setState({ ...this.state, isSubmitLater }, () => {
            this.triggerSubmit();
        });
    }

    private handleSubmit(model: Forms.Abstract.ComposeUploadsData) {
        const mediaForms = model.typeMedia
            .map((tm) => tm.media)
            .reduce((a, b) => a.concat(b))
            .filter((m) => !(m.id <= 0 && m.deleted) && m.id > 0);

        const body = {
            abstractId: model.abstractId,
            guid: this.props.abstract ? this.props.abstract.guid : undefined,
            isSaveLater: this.state.isSubmitLater,
            media: mediaForms.map((m, i) => ({
                abstractMediaId: m.id,
                caption: m.caption,
                deleted: m.deleted,
                mediaId: m.mediaId,
                tags: (m.tags || []).map((t: M.Tag.Tag) => ({
                    id: t.id,
                    name: t.name,
                })),
                title: m.title,
            })),
        };

        this.saveAbstract(model, body);
    }

    private handleValidation() {
        // do not validate required uploads here.
        return M.Abstract.validateUploadsStep(this.state.formModel, this.props.type !== undefined && this.props.type.uploadDeadline !== null);
    }

    private async saveAbstract(model: Forms.Abstract.ComposeUploadsData, body: any) {
        this.toggleProcessing(true);

        const result = await this.props.api.post(Api.endpoints.abstract.composeUploads, {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.props.onAbstractSaved(result.data, this.state.isSubmitLater);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
            this.toggleProcessing(false);
        }
    }

    private updateUploadStatus(abstractTypeMedia: number, isDone: boolean) {
        let uploadStatus = [...this.state.uploadStatus];
        if (isDone) {
            uploadStatus = uploadStatus.filter((x) => x !== abstractTypeMedia);
        }
        else {
            if (uploadStatus.indexOf(abstractTypeMedia) === -1)
                uploadStatus.push(abstractTypeMedia);
        }

        this.setState({ ...this.state, uploadStatus });
    }
}



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/components/UploadsForm.tsx